import React, { useEffect, useState } from 'react';
import {
  CharacterPortrait,
  SituationSortCategory,
  SituationSortSituation,
} from '../../types';
import { getVisibleTextFromInlineTerms } from '../../utils/inline-terms';
import { getSituationSortDisplayLabel } from '../../utils/situation-sort-label';
import { FormattedInlineText } from '../formatted-inline-text';
import { uniqueId } from './utils';
import type { SortCategoryActions } from './sort-category-actions';
import styles from './scenario-studio.module.css';
import { PedaCategorySelect } from './PedaCategorySelect';

function truncate(text: string, max = 60): string {
  const clean = getVisibleTextFromInlineTerms(text).replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

function categoryVisibleLabel(category: SituationSortCategory): string {
  return getVisibleTextFromInlineTerms(category.label) || category.id;
}

function useExpandableCards(ids: string[]) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const valid = new Set(ids);
    setExpandedIds((prev) => {
      const next = new Set([...prev].filter((id) => valid.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [ids]);

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expand = (id: string) => {
    setExpandedIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const isExpanded = (id: string) => expandedIds.has(id);

  return { isExpanded, toggleExpanded, expand };
}

interface SituationSortCategoriesEditorProps {
  categories: SituationSortCategory[];
  onChange: (categories: SituationSortCategory[]) => void;
  globalCategories?: SituationSortCategory[];
  onAddFromGlobal?: (categoryId: string) => void;
  onSyncedChange?: (categories: SituationSortCategory[]) => void;
}

export const SituationSortCategoriesEditor: React.FC<SituationSortCategoriesEditorProps> = ({
  categories,
  onChange,
  globalCategories = [],
  onAddFromGlobal,
  onSyncedChange,
}) => {
  const cardIds = categories.map((category, index) => category.id || `__cat_${index}`);
  const { isExpanded, toggleExpanded, expand } = useExpandableCards(cardIds);
  const [pickerValue, setPickerValue] = useState('');

  const update = (index: number, patch: Partial<SituationSortCategory>, sync = false) => {
    const next = categories.map((c, i) => (i === index ? { ...c, ...patch } : c));
    if (sync && onSyncedChange) onSyncedChange(next);
    else onChange(next);
  };

  const move = (index: number, dir: -1 | 1) => {
    const next = [...categories];
    const newIdx = index + dir;
    if (newIdx < 0 || newIdx >= next.length) return;
    const [moved] = next.splice(index, 1);
    if (!moved) return;
    next.splice(newIdx, 0, moved);
    onChange(next);
  };

  const addCategory = () => {
    const category = { id: uniqueId('cat'), label: 'Nouvelle catégorie' };
    if (onSyncedChange) onSyncedChange([...categories, category]);
    else onChange([...categories, category]);
    expand(category.id);
  };

  const availableGlobalCategories = globalCategories.filter(
    (category) => !categories.some((entry) => entry.id === category.id)
  );

  const handlePickFromGlobal = (categoryId: string) => {
    if (!categoryId || !onAddFromGlobal) return;
    onAddFromGlobal(categoryId);
    setPickerValue('');
  };

  return (
    <div className={styles.phoneSection}>
      <div className={styles.panelHeader}>
        <h5>Catégories</h5>
        <div className={styles.inlineActions}>
          {availableGlobalCategories.length > 0 && onAddFromGlobal && (
            <select
              className={styles.select}
              value={pickerValue}
              onChange={(e) => handlePickFromGlobal(e.target.value)}
              aria-label="Ajouter une catégorie depuis la liste globale"
            >
              <option value="">+ Depuis la liste</option>
              {availableGlobalCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {categoryVisibleLabel(category)}
                </option>
              ))}
            </select>
          )}
          <button type="button" className={styles.btnSecondary} onClick={addCategory}>
            + Catégorie
          </button>
        </div>
      </div>
      {categories.length === 0 && (
        <p className={styles.hint}>Ajoutez au moins une catégorie avant de créer des situations.</p>
      )}
      {categories.map((category, index) => {
        const cardId = cardIds[index]!;
        const expanded = isExpanded(cardId);
        const summary = categoryVisibleLabel(category);

        return (
          <div key={cardId} className={styles.card}>
            <div className={styles.cardHeader}>
              <button
                type="button"
                className={styles.expandToggle}
                onClick={() => toggleExpanded(cardId)}
                aria-expanded={expanded}
                aria-label={expanded ? 'Replier la catégorie' : 'Déplier la catégorie'}
                title={expanded ? 'Replier' : 'Développer'}
              >
                {expanded ? '▾' : '▸'}
              </button>
              <div className={styles.cardHeaderMain}>
                <h5 style={{ margin: 0 }}>Catégorie {index + 1}</h5>
                {!expanded && summary && <span className={styles.cardMeta}>{summary}</span>}
              </div>
              <div className={styles.inlineActions}>
                <button type="button" onClick={() => move(index, -1)} title="Monter">↑</button>
                <button type="button" onClick={() => move(index, 1)} title="Descendre">↓</button>
                <button
                  type="button"
                  onClick={() => onChange(categories.filter((_, i) => i !== index))}
                  title="Supprimer"
                >
                  ×
                </button>
              </div>
            </div>
            {expanded && (
              <div className={styles.cardBody}>
                <div className={styles.field}>
                  <label>Libellé affiché (supporte **terme**(définition))</label>
                  <input
                    className={styles.input}
                    value={category.label}
                    onChange={(e) => update(index, { label: e.target.value }, true)}
                    placeholder="📤 **Information exposée**(donnée visible sans protection)"
                  />
                  {category.label && (
                    <div className={styles.inlineTermPreview}>
                      <span className={styles.inlineTermPreviewLabel}>Aperçu :</span>
                      <FormattedInlineText text={category.label} />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

interface SituationSortSituationsEditorProps {
  situations: SituationSortSituation[];
  categories: SituationSortCategory[];
  onChange: (situations: SituationSortSituation[]) => void;
}

export const SituationSortSituationsEditor: React.FC<SituationSortSituationsEditorProps> = ({
  situations,
  categories,
  onChange,
}) => {
  const cardIds = situations.map((situation, index) => situation.id || `__sit_${index}`);
  const { isExpanded, toggleExpanded, expand } = useExpandableCards(cardIds);

  const update = (index: number, patch: Partial<SituationSortSituation>) => {
    onChange(situations.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  const move = (index: number, dir: -1 | 1) => {
    const next = [...situations];
    const newIdx = index + dir;
    if (newIdx < 0 || newIdx >= next.length) return;
    const [moved] = next.splice(index, 1);
    if (!moved) return;
    next.splice(newIdx, 0, moved);
    onChange(next);
  };

  const addSituation = () => {
    const id = uniqueId('situation');
    onChange([
      ...situations,
      {
        id,
        text: '',
        expectedCategoryId: categories[0]?.id ?? '',
        feedbackOk: '',
        feedbackKo: '',
      },
    ]);
    expand(id);
  };

  const categoryLabel = (categoryId: string) =>
    categoryVisibleLabel(categories.find((c) => c.id === categoryId) ?? { id: categoryId, label: '' });

  return (
    <div className={styles.phoneSection}>
      <div className={styles.panelHeader}>
        <h5>Situations à classer</h5>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={addSituation}
          disabled={categories.length === 0}
        >
          + Situation
        </button>
      </div>
      {categories.length === 0 && (
        <p className={styles.hint}>Créez d'abord au moins une catégorie.</p>
      )}
      {situations.map((situation, index) => {
        const cardId = cardIds[index]!;
        const expanded = isExpanded(cardId);
        const summaryParts = [
          situation.label?.trim()
            ? situation.label.trim()
            : situation.text
              ? truncate(situation.text)
              : getSituationSortDisplayLabel(situation, index),
          situation.expectedCategoryId
            ? `→ ${categoryLabel(situation.expectedCategoryId)}`
            : undefined,
        ].filter(Boolean);
        const summary = summaryParts.join(' ');

        return (
          <div key={cardId} className={styles.card}>
            <div className={styles.cardHeader}>
              <button
                type="button"
                className={styles.expandToggle}
                onClick={() => toggleExpanded(cardId)}
                aria-expanded={expanded}
                aria-label={expanded ? 'Replier la situation' : 'Déplier la situation'}
                title={expanded ? 'Replier' : 'Développer'}
              >
                {expanded ? '▾' : '▸'}
              </button>
              <div className={styles.cardHeaderMain}>
                <h5 style={{ margin: 0 }}>{getSituationSortDisplayLabel(situation, index)}</h5>
                {!expanded && summary && <span className={styles.cardMeta}>{summary}</span>}
              </div>
              <div className={styles.inlineActions}>
                <button type="button" onClick={() => move(index, -1)} title="Monter">↑</button>
                <button type="button" onClick={() => move(index, 1)} title="Descendre">↓</button>
                <button
                  type="button"
                  onClick={() => onChange(situations.filter((_, i) => i !== index))}
                  title="Supprimer"
                >
                  ×
                </button>
              </div>
            </div>
            {expanded && (
              <div className={styles.cardBody}>
                <div className={styles.field}>
                  <label>ID</label>
                  <input
                    className={styles.input}
                    value={situation.id}
                    onChange={(e) => update(index, { id: e.target.value })}
                    placeholder="situation_1"
                  />
                </div>
                <div className={styles.field}>
                  <label>Libellé affiché sur la carte</label>
                  <input
                    className={styles.input}
                    value={situation.label ?? ''}
                    onChange={(e) => update(index, { label: e.target.value || undefined })}
                    placeholder={getSituationSortDisplayLabel(situation, index)}
                  />
                </div>
                <div className={styles.field}>
                  <label>Texte de la situation</label>
                  <textarea
                    className={styles.textarea}
                    rows={4}
                    value={situation.text}
                    onChange={(e) => update(index, { text: e.target.value })}
                  />
                </div>
                <div className={styles.field}>
                  <label>Catégorie attendue</label>
                  <select
                    className={styles.select}
                    value={situation.expectedCategoryId}
                    onChange={(e) => update(index, { expectedCategoryId: e.target.value })}
                  >
                    <option value="">—</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {categoryVisibleLabel(c)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={styles.field}>
                  <label>Feedback (bonne réponse)</label>
                  <textarea
                    className={styles.textarea}
                    rows={2}
                    value={situation.feedbackOk}
                    onChange={(e) => update(index, { feedbackOk: e.target.value })}
                  />
                </div>
                <div className={styles.field}>
                  <label>Feedback (mauvaise réponse)</label>
                  <textarea
                    className={styles.textarea}
                    rows={2}
                    value={situation.feedbackKo}
                    onChange={(e) => update(index, { feedbackKo: e.target.value })}
                  />
                </div>
                <div className={styles.field}>
                  <label>Boucliers pédagogiques (bonne réponse)</label>
                  <input
                    type="number"
                    className={styles.input}
                    value={situation.pedaShields ?? ''}
                    onChange={(e) =>
                      update(index, { pedaShields: parseInt(e.target.value, 10) || undefined })
                    }
                  />
                </div>
                <div className={styles.field}>
                  <PedaCategorySelect
                    value={situation.pedaCategory ?? ''}
                    onChange={(pedaCategory) => update(index, { pedaCategory })}
                    required={(situation.pedaShields ?? 0) > 0}
                    label="Catégorie PEDA (sous-titre animation)"
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

interface SituationSortGameEditorProps {
  item: {
    instructionText?: string;
    speakerName?: string;
    speakerCharacterId?: string;
    requireAllCorrect?: boolean;
    categories: SituationSortCategory[];
    situations: SituationSortSituation[];
  };
  characters: CharacterPortrait[];
  onChange: (patch: Partial<SituationSortGameEditorProps['item']>) => void;
  sortCategoryActions?: SortCategoryActions;
  sceneId?: string;
}

export const SituationSortGameEditor: React.FC<SituationSortGameEditorProps> = ({
  item,
  characters,
  onChange,
  sortCategoryActions,
  sceneId,
}) => (
  <>
    <div className={styles.phoneSection}>
      <h5>Paramètres généraux</h5>
      <div className={styles.field}>
        <label>Consigne</label>
        <textarea
          className={styles.textarea}
          rows={2}
          value={item.instructionText ?? ''}
          onChange={(e) => onChange({ instructionText: e.target.value || undefined })}
          placeholder="Glissez chaque situation dans la catégorie adaptée."
        />
      </div>
      <div className={styles.field}>
        <label>Nom de l'intervenant (affiché)</label>
        <input
          className={styles.input}
          value={item.speakerName ?? ''}
          onChange={(e) => onChange({ speakerName: e.target.value || undefined })}
          placeholder="RSSI"
        />
      </div>
      <div className={styles.field}>
        <label>Personnage intervenant (portrait)</label>
        <select
          className={styles.select}
          value={item.speakerCharacterId ?? ''}
          onChange={(e) => onChange({ speakerCharacterId: e.target.value || undefined })}
        >
          <option value="">—</option>
          {characters.map((c) => (
            <option key={c.id} value={c.id}>{c.id} — {c.name ?? c.id}</option>
          ))}
        </select>
      </div>
      <div className={styles.field}>
        <label>
          <input
            type="checkbox"
            checked={item.requireAllCorrect === true}
            onChange={(e) => onChange({ requireAllCorrect: e.target.checked || undefined })}
          />
          {' '}Exiger toutes les bonnes réponses pour continuer (sinon réessayer)
        </label>
      </div>
    </div>

    <SituationSortCategoriesEditor
      categories={item.categories}
      onChange={(categories) => onChange({ categories })}
      globalCategories={sortCategoryActions?.globalCategories}
      onAddFromGlobal={
        sortCategoryActions && sceneId
          ? (categoryId) => sortCategoryActions.addFromGlobal(sceneId, categoryId)
          : undefined
      }
      onSyncedChange={
        sortCategoryActions && sceneId
          ? (categories) => sortCategoryActions.setGameCategories(sceneId, categories)
          : undefined
      }
    />

    <SituationSortSituationsEditor
      situations={item.situations}
      categories={item.categories}
      onChange={(situations) => onChange({ situations })}
    />
  </>
);
